const { Default: _, match } = require('@nike/rcf-fp');

const { loadBodyTranslations } = require('../../../redux/actions');
const {
 NPOS, ERROR_CODES, WORKSTATION_TYPE_CODES, TRANSLATIONS
} = require('../../../utils/constants');
const { sortItems, parseNposResponse, getLocaleOverride } = require('../../../utils/ej-utils');

const IGNORE_FOR_CHECKING_APPLIED_FILTERS = ['cardNo', 'transactionType', 'currentStore'];
const IGNORE_FOR_MORE_FILTERS_COUNT = ['transactionType', 'maximumAmount', 'minimumAmount'];

const getFilterLabelText = (filter, props, translations) => {
    switch (filter) {
        case NPOS.FILTERS.PURCHASE_DATE: {
            const { dateRange: { startDate, endDate }, isDateSelected, moment } = props;
            return match()(
                [() => !isDateSelected, translations.RL_PurchaseDate],
                [() => startDate && endDate, moment(startDate).isSame(endDate, 'day') ? `${moment(startDate).format('MMM D')}` : `${moment(startDate).format('MMM D')} - ${moment(endDate).format('MMM D')}`],
                [() => startDate, `${moment(startDate).format('MMM D')}`],
                [_, null]
            );
        }
        case NPOS.FILTERS.PAYMENT_TYPE: {
            const { paymentType, isFilterApplied } = props;
            const RlPayment = translations.RL_Payment;
            if (!paymentType || !isFilterApplied) return RlPayment;
            return match()(
                [() => paymentType === NPOS.PAYMENT_TYPE.CREDIT_DEBIT_CARD, `${RlPayment}: ${translations.RL_Card}`],
                [() => paymentType === NPOS.PAYMENT_TYPE.CASH, `${RlPayment}: ${translations.UI_Cash}`],
                [() => paymentType === NPOS.PAYMENT_TYPE.GIFT_CARD, `${RlPayment}: ${translations.GiftCard}`],
                [() => paymentType === NPOS.PAYMENT_TYPE.COMPLIMENTARY, `${RlPayment}: ${translations.RL_Complimentary}`],
                [_, 'Payment'],
                );
        }
        case NPOS.FILTERS.UPC: {
            const { upcNo } = props;
            return upcNo ? `${translations.RL_UPC}: ${upcNo.toString().substring(0, 3)}...` : translations.RL_UPC;
        }
        default:
            return null;
    }
};

const isFilterOpen = (filter, openFilter) => !openFilter || openFilter === filter;

const sortFn = (a, b, sortBy, sortOrder, formatType) => {
    switch (formatType) {
        case 'number': return (sortOrder === 'asc' ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy]);
        default: return (sortOrder === 'asc' ? a[sortBy]?.toString()?.localeCompare(b[sortBy]) : b[sortBy]?.toString()?.localeCompare(a[sortBy]));
    }
};

const isDay = (day, dateTime, timezone, moment, dateFormat) => {
    const date = dateTime.split(' ')[0];
    const today = moment().tz(timezone).format(dateFormat);

    if (day === 'today') return moment(date, dateFormat).isSame(moment(today, dateFormat), 'day');
    if (day === 'yesterday') return moment(date, dateFormat).isSame(moment(today, dateFormat).subtract(1, 'days'), 'day');
    return false;
};

const getFormattedDateAndTime = (dateTime, moment, dateFormat) => ({
        date: moment(dateTime, dateFormat).format('MMM D'),
        time: `${dateTime.split(' ')[1]} ${dateTime.split(' ')?.[2]}`
    });

const toNposDateTimeFormat = (dateTime, timezone, moment, dateFormat, translations) => {
    if (!dateTime) return { date: null, time: null };

    const { time, date } = getFormattedDateAndTime(dateTime, moment, dateFormat);

    if (isDay('today', dateTime, timezone, moment, dateFormat)) return { date: `${translations.RL_Today}, ${date}`, time };
    if (isDay('yesterday', dateTime, timezone, moment, dateFormat)) return { date: `${translations.RL_Yesterday}, ${date}`, time };
    else return { date, time };
};

const getColAlignment = (colNo, totalCols) => {
    if (colNo === 0) return 'fs';
    if (colNo === totalCols - 1) return 'fe';
    return 'c';
  };

const toNumber = (value) => parseFloat(value?.replace(/\D/g, '')) || '';

const getAdvancedFiltersCount = (filters) => {
    const filterToConsider = Object.keys(NPOS.FILTERS.MORE_FILTERS_CONFIG).filter((filter) => !IGNORE_FOR_MORE_FILTERS_COUNT.includes(filter));
    const filtersApplied = [...Object.keys(filters).filter((key) => (filterToConsider).includes(key) && filters[key]), ...(filters.maximumAmount || filters.minimumAmount ? ['transactionAmount'] : [])];
    return filtersApplied.length;
  };

const isAnyFilterApplied = (filtersApplied) => Object.keys(filtersApplied).filter((filter) => !IGNORE_FOR_CHECKING_APPLIED_FILTERS.includes(filter)).some((key) => filtersApplied[key]);

const isAnyFilterError = (errors, filterGroup = 'ALL') => {
    const MORE_FILTER_ERRORS = !!(errors.emailId || errors.transactionAmount);

    if (filterGroup === 'MORE') return MORE_FILTER_ERRORS;
    return !!(errors.selectedDates || errors.CreditDebit || errors.GiftCard || errors.upcNo || errors.transactionAmount || MORE_FILTER_ERRORS);
};

const getFilterKeyById = (fieldId) => NPOS.FILTERS.MORE_FILTERS_CONFIG[fieldId]?.searchKey || fieldId;

/** This will generate the reset state for the More Filters */
const getDefaultStateForMoreFilters = () => {
    const initialState = {};
    const { MORE_FILTERS_CONFIG } = NPOS.FILTERS;
    return Object.keys(MORE_FILTERS_CONFIG).reduce((acc, id) => ({ ...acc, [id]: MORE_FILTERS_CONFIG[id].defaultValue }), initialState);
};

const getFilterClassName = (filterId, openFilter, isFilterApplied, open) => {
    const isNoFilterOpen = !openFilter;
    const isAnotherFilterOpen = openFilter !== filterId;

    const classsName = `${(isNoFilterOpen && !isFilterApplied) ? 'default-filter-state' : ''} ${open ? 'open-filter' : ''} ${isFilterApplied ? 'applied-filter' : ''} ${isAnotherFilterOpen && !isNoFilterOpen ? 'inactive-filter' : ''}`;
    return classsName.trim();
  };

const printErrorCode = (msg) => `ERROR : ${msg}`;

const getTotalItemsPurchased = (row) => {
  const transactionStatus = row?.transaction?.Transaction?.OrderTransaction?.TransactionStatus;
  if (transactionStatus === 'Suspended' || transactionStatus === 'Canceled') return 0;
  const sortedItems = sortItems(row?.transaction?.Transaction?.RetailTransaction?.LineItems || row?.transaction?.Transaction?.OrderTransaction?.LineItems || []);
  const purchasedItems = [
    ...(sortedItems?.Delivery?.map((item) => ({ ...item.SaleForDelivery, voidFlag: item.VoidFlag })) || []),
    ...(sortedItems?.Pickup?.map((item) => ({ ...item.SaleForPickup, voidFlag: item.VoidFlag })) || []),
    ...(sortedItems?.Sales?.map((item) => ({ ...item.Sale, voidFlag: item.VoidFlag })) || [])
  ];
  const stockItemsCount = purchasedItems.reduce((acc, item) => acc + (item?.ItemType === 'Stock' && !item?.voidFlag ? (item?.Quantity?.Quantity || 0) : 0), 0);

  return stockItemsCount;
};

  const parseErrorResponse = (err, store) => {
    const { PRINTER_NOT_FOUND, INVALID_RECEIPT_IMAGE } = NPOS.ERROR_CODES;
    const { localizedBody } = store.getState();
    switch (err) {
      case PRINTER_NOT_FOUND:
        return localizedBody?.RL_ErrPrinterNotConnected;
      case INVALID_RECEIPT_IMAGE:
          return printErrorCode(ERROR_CODES?.E_INVALID_RECEIPT_IMAGE);
      default:
        // NPOS returned unexpected error
        return `${localizedBody?.RL_ErrUnexpectedError} ${err}`;
    }
  };

const getParsedObject = (obj) => {
  try {
    const parsedObj = parseNposResponse(obj);
    if (typeof parsedObj === 'object' && parsedObj !== null) return parsedObj;
    return obj;
  } catch (ex) {
    return obj;
  }
};

const parseData = (message, payload) => {
  const parsedObject = getParsedObject(payload);

  return ({
      data: parsedObject,
      messageId: message,
    });
};

// eslint-disable-next-line no-console
const unhandledNposMessage = (messageId) => console.warn(`Received unhandled message from NPOS: ${messageId}, ignoring...`);

const handlePrintResponse = (messageId, data, store) => {
  const { SUCCESS, ERROR } = NPOS.PRINT_RESPONSE;
  switch (messageId) {
    case SUCCESS: {
      store && store.dispatch({ nposValues: { printJobInfo: { ...store.nposValues?.printJobInfo, status: NPOS.PRINT_STATUS.SUCCESS } }, type: 'SET_NPOS_VALUES' });
      break;
    }
    case ERROR: {
      const error = parseErrorResponse(data, store);
      store && store.dispatch({ nposValues: { printJobInfo: { ...store.nposValues?.printJobInfo, error, status: NPOS.PRINT_STATUS.ERROR } }, type: 'SET_NPOS_VALUES' });
      break;
    }
    default:
        // NPOS returned Unknown print status:  Refer NPOS.PRINT_RESPONSE
        unhandledNposMessage(messageId);
        store && store.dispatch({
          nposValues: {
          printJobInfo: {
          ...store.nposValues?.printJobInfo, error: printErrorCode(`${ERROR_CODES?.E_UNKNOWN_NPOS_PRINT_STATUS} (${messageId})`), status: NPOS.PRINT_STATUS.ERROR
          }
        },
       type: 'SET_NPOS_VALUES'
      });
      break;
  }
};

const handleActionChangeLanguage = (data, store) => {
  // language changed in NPOS
  const { data: { locale: nposLocale } } = data;
  if (store && nposLocale) {
    const nposLocaleFinal = getLocaleOverride(nposLocale);
    store.dispatch(loadBodyTranslations(nposLocaleFinal || 'en-US'));
  }
};

const handleActionSessionContext = (data, store) => {
  const sessionContext = data?.data;
  if (store && sessionContext) {
    store.dispatch({
      nposValues: { sessionContext },
      type: 'SET_NPOS_VALUES',
    });
  }
};

const isNposPrintResponse = (messageId) => Object.values(NPOS.PRINT_RESPONSE).includes(messageId);

const onReceiveNposMessage = (message, payload, store) => {
    const { messageId, data } = parseData(message, payload);

    return match(messageId)(
      [() => isNposPrintResponse(messageId), () => handlePrintResponse(messageId, data, store)],
      [NPOS.ACTIONS.CHANGE_LANGUAGE, () => handleActionChangeLanguage(data, store)],
      [NPOS.ACTIONS.SESSION_CONTEXT, () => handleActionSessionContext(data, store)],
      [_, unhandledNposMessage]
    );
  };

  const checkReprintAvailability = (transaction) => {
    const isNposRegister = Object.values(WORKSTATION_TYPE_CODES).includes(transaction?.Workstation?.TypeCode);

    const isReprintAvailable = isNposRegister;
    const message = match()(
      [() => isReprintAvailable, null],
      [() => !isNposRegister, TRANSLATIONS.REPRINT_UNAVAILABLE.INVALID_WORKSTATION_TYPE],
      [_, null]
    );

    return {
      isReprintAvailable,
      message
    };
  };

module.exports = {
    checkReprintAvailability, getAdvancedFiltersCount, getColAlignment, getDefaultStateForMoreFilters, getFilterClassName, getFilterKeyById, getFilterLabelText, getTotalItemsPurchased, isAnyFilterApplied, isAnyFilterError, isFilterOpen, onReceiveNposMessage, sortFn, toNposDateTimeFormat, toNumber
};
